import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiGet, apiPost } from '../utils/apiAuth';
import config from '../config';
import CONSTANTS, { EditorConstants, ContentTypeConstants, normalizeEditorKey } from '../constants/contentCreationConstants';
import {
  createDefaultQuizOptions,
  parseQuizQuestionData,
  QUIZ_TEMPLATES,
} from '../utils/quizForm';
import { getTopicsSubtopicsBySubject } from '../utils/topicsSubtopicsCache';

export const useContentCreation = (data) => {
  const [subjectId, setSubjectId] = useState(data?.subject_id || "");
  const [subjectName, setSubjectName] = useState(data?.subject_name || "");
  const freezeFilters = data?.freezeFilters || false;

  // Session storage keys
  const SESSION_KEYS = {
    SELECTED_TOPIC: 'contentCreation_selectedTopic',
    SELECTED_SUBTOPIC: 'contentCreation_selectedSubTopic',
    SELECTED_DIFFICULTY: 'contentCreation_selectedDifficulty',
    CONTENT_TYPE: 'contentCreation_contentType',
    SELECTED_VIDEO: 'contentCreation_selectedVideo',
    SELECTED_NOTE: 'contentCreation_selectedNote',
    SELECTED_NOTEBOOK: 'contentCreation_selectedNotebook',
    SELECTED_QUESTION: 'contentCreation_selectedQuestion',
    SELECTED_EDITOR: 'contentCreation_selectedEditor'
  };

  // Helper functions for session storage
  const saveToSession = (key, value) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
    }
  };

  const loadFromSession = (key, defaultValue = "") => {
    try {
      const stored = sessionStorage.getItem(key);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch (error) {
      return defaultValue;
    }
  };

  // State management with session storage initialization
  const [videoFile, setVideoFile] = useState(null);
  const [videoDescription, setVideoDescription] = useState("");
  const [videoTime, setVideoTime] = useState("");
  const [videoLevel, setVideoLevel] = useState("level1");
  const [files, setFiles] = useState([{ file: null, text: "", time: "0", level: "level1" }]);
  const [count, setCount] = useState({});
  const [explain, setExplain] = useState([]);
  const [showSucModal, setShowSucModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDifficulty, setSelectedDifficulty] = useState(() => loadFromSession(SESSION_KEYS.SELECTED_DIFFICULTY, "default"));
  const [selectedQuestion, setSelectedQuestion] = useState(() => loadFromSession(SESSION_KEYS.SELECTED_QUESTION, ""));
  const [codingQuestionText, setCodingQuestionText] = useState(""); // Separate state for coding question text
  const [currentFile, setCurrentFile] = useState(CONSTANTS.DEFAULT_QN);
  const [hints, setHints] = useState([]);
  const [isLoadingQuestion, setIsLoadingQuestion] = useState(false); // Guard to prevent multiple simultaneous API calls
  const [refreshKey, setRefreshKey] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [testCases, setTestCases] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [tags, settags] = useState([]);
  const [mcqtags, setmcqtags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [examples, setExamples] = useState([]);
  const [defaultTemplate, setdefaultTemplate] = useState("");
  const [code1, setCode1] = useState("");
  const [code2, setCode2] = useState("");
  const [DisplayFiles, setDisplayFiles] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(() => loadFromSession(SESSION_KEYS.SELECTED_TOPIC, ""));
  const [topics, setTopics] = useState([]);
  const [subTopics, setSubTopics] = useState([]);
  const [contentType, setContentType] = useState(() => {
    // If freezeFilters is true, prioritize location.state over sessionStorage
    const freezeFiltersFromData = data?.freezeFilters || false;
    if (freezeFiltersFromData && data?.content_type) {
      return data.content_type;
    }
    const sessionContentType = loadFromSession(SESSION_KEYS.CONTENT_TYPE, "default");
    if (sessionContentType && sessionContentType !== "default") {
      return sessionContentType;
    }
    return "default";
  });
  const [mcqQuestion, setMCQQuestion] = useState("");
  const [options, setOptions] = useState({
    option1: "",
    option2: "",
    option3: "",
    option4: "",
  });
  const [quizOptions, setQuizOptions] = useState([
    { id: 'opt_a', text: '', correct: true },
    { id: 'opt_b', text: '', correct: false },
  ]);
  const [quizTemplate, setQuizTemplate] = useState('single_choice');
  const [error, setError] = useState("");
  const [mcqExplanation, setMCQExplanation] = useState("");
  const [selectedSubTopic, setSelectedSubTopic] = useState(() => loadFromSession(SESSION_KEYS.SELECTED_SUBTOPIC, ""));
  const [selectedVideo, setSelectedVideo] = useState(() => loadFromSession(SESSION_KEYS.SELECTED_VIDEO, ""));
  const [selectedNote, setSelectedNote] = useState(() => loadFromSession(SESSION_KEYS.SELECTED_NOTE, ""));
  const [selectedNotebook, setSelectedNotebook] = useState(() => loadFromSession(SESSION_KEYS.SELECTED_NOTEBOOK, ""));
  const [SQLTestCases, setSQLTestCases] = useState([]);
  const [isHtmlModalOpen, setIsHtmlModalOpen] = useState(false);
  const [htmlContent, setHtmlContent] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState('1');
  const [sqldata, setsqlData] = useState({});
  const [sqlerror, setsqlError] = useState(null);
  const [selectedTables, setSelectedTables] = useState([]);
  const [sqlexplain, setsqlExplain] = useState([]);
  const [sqltags, setsqlTags] = useState([]);
  const [sqlhints, setsqlHints] = useState([]);
  const [sqlquery, setsqlQuery] = useState("");
  const [sqlqn, setsqlQn] = useState("");
  const [sqltable, setTable] = useState("");
  const [sqlExpectedOutput, setSqlExpectedOutput] = useState([]);
  const [isQueryrun, setIsQueryrun] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalMessage, setModalMessage] = useState('');
  const [resShowModal, setResShowModal] = useState(false);
  const pendingLookupRef = useRef(null);
  
  // Notes specific state
  const [notesLevel, setNotesLevel] = useState("");
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [allNotes, setAllNotes] = useState({});
  const [notesTitle, setNotesTitle] = useState("");
  const [notesContent, setNotesContent] = useState("");
  const [notesTime, setNotesTime] = useState("");
  const [notesStatus, setNotesStatus] = useState(CONSTANTS.ACTIVE);
  const [notesSequence, setNotesSequence] = useState("");
  const [notebookSource, setNotebookSource] = useState("");
  const [notebookId, setNotebookId] = useState(null);
  const [notebookTitle, setNotebookTitle] = useState("");
  const [notebookSequence, setNotebookSequence] = useState("1");
  const [notebookTime, setNotebookTime] = useState("");
  const [notebookStatus, setNotebookStatus] = useState(CONSTANTS.ACTIVE);

  // Video specific state (using existing video state variables)
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [allVideos, setAllVideos] = useState({});
  const [videoTitle, setVideoTitle] = useState("");
  const [videoStatus, setVideoStatus] = useState(CONSTANTS.ACTIVE);
  const [videoSequence, setVideoSequence] = useState("");
  const [videoDetails, setVideoDetails] = useState(null);
  const [selectedEditor, setSelectedEditor] = useState(() =>
    normalizeEditorKey(loadFromSession(SESSION_KEYS.SELECTED_EDITOR, ""))
  );

  const isContentTypeDisabled = freezeFilters ? false : (!selectedTopic || !selectedSubTopic);

  // Helper function to check if content type is MCQ or Quiz
  const isMCQ = (contentType) => {
    return (
      contentType === ContentTypeConstants.MCQ.key ||
      contentType === ContentTypeConstants.QUIZ.key
    );
  };

  const isQuiz = (contentType) => {
    return contentType === ContentTypeConstants.QUIZ.key;
  };

  // Helper function to check if content type is Coding
  const isCoding = (contentType) => {
    return contentType === ContentTypeConstants.CODING.key;
  };

  // Wrapper functions to save to session storage when state changes
  const setSelectedTopicWithSession = useCallback((value) => {
    setSelectedTopic(value);
    saveToSession(SESSION_KEYS.SELECTED_TOPIC, value);
  }, []);

  const setSelectedSubTopicWithSession = useCallback((value) => {
    setSelectedSubTopic(value);
    saveToSession(SESSION_KEYS.SELECTED_SUBTOPIC, value);
  }, []);

  const setSelectedDifficultyWithSession = useCallback((value) => {
    // Debug: log every time difficulty is updated to trace unexpected resets
    try {
    } catch (e) {
      // ignore console errors in non-browser environments
    }
    setSelectedDifficulty(value);
    saveToSession(SESSION_KEYS.SELECTED_DIFFICULTY, value);
  }, []);

  const setContentTypeWithSession = useCallback((value) => {
    setContentType(value);
    saveToSession(SESSION_KEYS.CONTENT_TYPE, value);
    // Reset current file when switching content types
    setCurrentFile(CONSTANTS.DEFAULT_QN);
    
    // Clear all form fields when switching content types
    setVideoDescription("");
    setVideoTime("");
    setFiles([{ file: null, text: "", time: "0", level: "level1" }]);
    setSelectedQuestion("");
    setdefaultTemplate("");
    setCode1("");
    setCode2("");
    setMCQQuestion("");
    setError("");
    setMCQExplanation("");
    setOptions({
      option1: "",
      option2: "",
      option3: "",
      option4: ""
    });
    setHtmlContent("");
    setsqlQuery("");
    setsqlQn("");
    setTable("");
    setSqlExpectedOutput([]);
    setNotesTitle("");
    setNotesContent("");
    setNotesTime("");
    setNotesSequence("");
    setNotebookSource("");
    setNotebookId(null);
    setNotebookTitle("");
    setNotebookSequence("1");
    setNotebookTime("");
    setNotebookStatus(CONSTANTS.ACTIVE);
    setVideoTitle("");
    setVideoSequence("");
    setVideoDetails(null); // Clear video details to stop video player
    
    // Clear all array states for coding and other content types
    setExplain([]);
    setHints([]);
    setTestCases([]);
    setKeywords([]);
    settags([]);
    setmcqtags([]);
    setExamples([]);
    setSQLTestCases([]);
    setSelectedTables([]);
    setsqlExplain([]);
    setsqlTags([]);
    setsqlHints([]);
    // Clear editor when switching content types
    setSelectedEditor("");
    saveToSession(SESSION_KEYS.SELECTED_EDITOR, "");
  }, []);

  const setSelectedVideoWithSession = useCallback((value) => {
    setSelectedVideo(value);
    saveToSession(SESSION_KEYS.SELECTED_VIDEO, value);
  }, []);

  const setSelectedNoteWithSession = useCallback((value) => {
    setSelectedNote(value);
    saveToSession(SESSION_KEYS.SELECTED_NOTE, value);
  }, []);

  const setSelectedNotebookWithSession = useCallback((value) => {
    setSelectedNotebook(value);
    saveToSession(SESSION_KEYS.SELECTED_NOTEBOOK, value);
  }, []);

  const setSelectedQuestionWithSession = useCallback((value) => {
    setSelectedQuestion(value);
    saveToSession(SESSION_KEYS.SELECTED_QUESTION, value);
  }, []);

  const setSelectedEditorWithSession = useCallback((value) => {
    const normalized = normalizeEditorKey(value) || value || "";
    setSelectedEditor(normalized);
    saveToSession(SESSION_KEYS.SELECTED_EDITOR, normalized);
  }, []);

  // Effect to initialize content type from location.state data if provided (e.g., from ProjectContentBank)
  // This should run early to ensure content type is set before other initializations
  useEffect(() => {
    if (freezeFilters) {
    // First check if contentType is already set from sessionStorage
    const sessionContentType = loadFromSession(SESSION_KEYS.CONTENT_TYPE, "");
      
      // Priority: location.state content_type > sessionStorage content_type
      const contentTypeToUse = data?.content_type || sessionContentType;
      if (contentTypeToUse && contentTypeToUse !== "default") {
        // Always update if different from current state
        // Use a ref or check current state value to avoid unnecessary updates
        if (contentType !== contentTypeToUse) {
          setContentTypeWithSession(contentTypeToUse);
        } else {
        }
      } else {
      }
    }
  }, [data?.content_type, freezeFilters, contentType]); // Added contentType back to detect when it changes

  // Early initialization effect - sets values from location.state if sessionStorage is missing
  // This ensures values are set even if sessionStorage wasn't populated correctly
  // Runs once on mount when freezeFilters is true
  useEffect(() => {
    if (freezeFilters && data) {
      
      // Initialize content type FIRST - this is critical for other initializations
      const sessionContentType = loadFromSession(SESSION_KEYS.CONTENT_TYPE, "");
      const contentTypeToUse = data?.content_type || sessionContentType;
      if (contentTypeToUse && contentTypeToUse !== "default" && contentType !== contentTypeToUse) {
        setContentTypeWithSession(contentTypeToUse);
      } else if (contentTypeToUse && contentTypeToUse !== "default") {
      }
      
      // Initialize topic if not already set and available in location.state
      if (data.topic_id && !selectedTopic) {
        const topicId = String(data.topic_id);
        setSelectedTopicWithSession(topicId);
      }
      
      // Initialize subtopic if not already set and available in location.state
      if (data.sub_topic_id && !selectedSubTopic) {
        const subtopicId = String(data.sub_topic_id);
        setSelectedSubTopicWithSession(subtopicId);
      }
      
      // Initialize editor for coding questions from location.state (e.g., from ProjectContentBank question_data.editor)
      const isCodingFromData = data.content_type === ContentTypeConstants.CODING.key || data.content_type === ContentTypeConstants.PROJECT_CODING.key;
      if (isCodingFromData && data.editor) {
        const editorValue = normalizeEditorKey(data.editor);
        const sessionEditor = normalizeEditorKey(loadFromSession(SESSION_KEYS.SELECTED_EDITOR, ""));
        if (editorValue && (!sessionEditor || sessionEditor !== editorValue)) {
          setSelectedEditorWithSession(editorValue);
        }
      }

      // Initialize selected content if available in location.state
      // When freezeFilters is true, prioritize location.state.contentId over sessionStorage
      // Use data.content_type first (from location.state), then fall back to contentType state
      if (data.contentId) {
        const contentId = String(data.contentId);
        const contentTypeToCheck = data.content_type || contentType;
        
        if (contentTypeToCheck === ContentTypeConstants.VIDEO.key) { 
          // When freezeFilters is true, always use contentId from location.state
          if (freezeFilters && selectedVideo !== contentId) {
            setSelectedVideoWithSession(contentId);
          } else if (!selectedVideo) {
            setSelectedVideoWithSession(contentId);
          }
        } else if (contentTypeToCheck === ContentTypeConstants.NOTES.key) {
          if (freezeFilters && selectedNote !== contentId) {
            setSelectedNoteWithSession(contentId);
          } else if (!selectedNote) {
            setSelectedNoteWithSession(contentId);
          }
        } else if (contentTypeToCheck === ContentTypeConstants.NOTEBOOK.key) {
          if (freezeFilters && selectedNotebook !== contentId) {
            setSelectedNotebookWithSession(contentId);
          } else if (!selectedNotebook) {
            setSelectedNotebookWithSession(contentId);
          }
        } else if (contentTypeToCheck === ContentTypeConstants.MCQ.key || contentTypeToCheck === ContentTypeConstants.QUIZ.key || contentTypeToCheck === ContentTypeConstants.CODING.key) {
          // When freezeFilters is true, always use contentId from location.state for questions
          if (freezeFilters && selectedQuestion !== contentId) {
            setSelectedQuestionWithSession(contentId);
          } else if (!selectedQuestion) {
            setSelectedQuestionWithSession(contentId);
          }
        }
      } else {
      }
    }
    // Run when freezeFilters or data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freezeFilters, data?.contentId, data?.content_type, data?.editor]);

  // Effect to initialize selected content (video/note/question) from location.state data if provided
  // This runs after content type is set
  // When freezeFilters is true, prioritize location.state.contentId over sessionStorage values
  useEffect(() => {
    if (freezeFilters && data?.contentId && contentType && contentType !== "default") {
      const contentId = String(data.contentId);
      const contentTypeLower = String(contentType).toLowerCase();
      
      // Initialize based on content type (case-insensitive check)
      // When freezeFilters is true, always update to match location.state.contentId
      if (contentType === ContentTypeConstants.VIDEO.key) {
        if (selectedVideo !== contentId) {
          setSelectedVideoWithSession(contentId);
        }
      } else if (contentType === ContentTypeConstants.NOTES.key) {
        if (selectedNote !== contentId) {
          setSelectedNoteWithSession(contentId);
        }
      } else if (contentType === ContentTypeConstants.NOTEBOOK.key) {
        if (selectedNotebook !== contentId) {
          setSelectedNotebookWithSession(contentId);
        }
      } else if (contentType === ContentTypeConstants.MCQ.key || contentType === ContentTypeConstants.QUIZ.key || contentType === ContentTypeConstants.CODING.key) {
        if (selectedQuestion !== contentId) {
          setSelectedQuestionWithSession(contentId);
        } else {
        }
      }
    } else if (freezeFilters && !data?.contentId) {
    }
  }, [freezeFilters, data?.contentId, contentType, selectedVideo, selectedNote, selectedQuestion, setSelectedVideoWithSession, setSelectedNoteWithSession, setSelectedQuestionWithSession]);

  // Effect to initialize topic from location.state data if provided (runs after topics are loaded)
  useEffect(() => {
    
    // Only initialize if freezeFilters is true, we have the necessary data, and topics are loaded
    if (freezeFilters && data?.topic_id && topics.length > 0) {
      const topicId = String(data.topic_id);
      // Validate that the topic exists in the loaded topics before setting
      const topicExists = topics.some(topic => String(topic.topic_id) === topicId);
      if (topicExists && selectedTopic !== topicId) {
        setSelectedTopicWithSession(topicId);
      } else if (!topicExists) {
        // Topic doesn't exist, no action needed
      }
    }
  }, [freezeFilters, data?.topic_id, topics.length, topics, selectedTopic, setSelectedTopicWithSession]);

  // Effect to initialize subtopic from location.state data if provided (runs after topics and subtopics are loaded)
  useEffect(() => {
    
    // Only initialize if freezeFilters is true, we have the necessary data, topic is set, and subtopics are loaded
    // Note: subtopics are loaded by the effect at line 718 when selectedTopic changes
    if (freezeFilters && data?.sub_topic_id && selectedTopic && topics.length > 0 && subTopics.length > 0) {
      const subtopicId = String(data.sub_topic_id);
      // Validate that the subtopic exists in the loaded subtopics before setting
      const subtopicExists = subTopics.some(subtopic => String(subtopic.subtopic_id) === subtopicId);
      if (subtopicExists && selectedSubTopic !== subtopicId) {
        setSelectedSubTopicWithSession(subtopicId);
      } else if (!subtopicExists) {
        // Subtopic doesn't exist, no action needed
      }
    }
  }, [freezeFilters, data?.sub_topic_id, topics.length, subTopics.length, subTopics, selectedTopic, selectedSubTopic, setSelectedSubTopicWithSession]);

  // Keep subject in sync when navigation state provides a new one
  useEffect(() => {
    if (data?.subject_id) {
      setSubjectId(data.subject_id);
    }
    if (data?.subject_name) {
      setSubjectName(data.subject_name);
    }
  }, [data?.subject_id, data?.subject_name]);

  // API functions
  const fetchCount = useCallback(async (overrideSubjectId) => {
    const sid = overrideSubjectId || subjectId;
    if (!sid) return;
    try {
      const response = await apiGet(`${config.API_BASE_URL}/api/content_creation/get_count_of_questions/${sid}/`);
      setCount(response.data.count);
    } catch (error) {
    }
  }, [subjectId]);

  const fetchTopics = useCallback(async (overrideSubjectId) => {
    const sid = overrideSubjectId || subjectId || '';
    if (!sid) {
      setTopics([]);
      return [];
    }
    try {
      const nextTopics = await getTopicsSubtopicsBySubject(sid);
      setTopics(nextTopics);
      return nextTopics;
    } catch (error) {
      return [];
    }
  }, [subjectId]);

  // Load an item opened from sidebar search (filters stay editable)
  useEffect(() => {
    if (freezeFilters || !data?.contentId) return;

    const contentId = String(data.contentId);
    const resolvedType = data.content_type || contentType;
    const resolvedEditor =
      resolvedType === ContentTypeConstants.CODING.key
        ? normalizeEditorKey(data.editor)
        : "";
    const topicId = data.topic_id != null ? String(data.topic_id) : "";
    const subTopicId = data.sub_topic_id != null ? String(data.sub_topic_id) : "";

    pendingLookupRef.current = {
      contentId,
      contentType: resolvedType,
      editor: resolvedEditor,
      topicId,
      subTopicId,
    };

    if (data.subject_id && data.subject_id !== subjectId) {
      setSubjectId(data.subject_id);
      setSubjectName(data.subject_name || "");
      fetchTopics(data.subject_id);
      fetchCount(data.subject_id);
    } else if (data.subject_name) {
      setSubjectName(data.subject_name);
    }

    if (resolvedType && resolvedType !== contentType) {
      setContentTypeWithSession(resolvedType);
    }
    if (resolvedType === ContentTypeConstants.CODING.key && resolvedEditor) {
      setSelectedEditorWithSession(resolvedEditor);
    }
    if (topicId) {
      setSelectedTopicWithSession(topicId);
    }
    if (subTopicId) {
      setSelectedSubTopicWithSession(subTopicId);
    }

    setCurrentFile(CONSTANTS.DEFAULT_QN);

    if (resolvedType === ContentTypeConstants.VIDEO.key) {
      setSelectedVideoWithSession(contentId);
    } else if (resolvedType === ContentTypeConstants.NOTES.key) {
      setSelectedNoteWithSession(contentId);
    } else {
      setSelectedQuestionWithSession(contentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freezeFilters, data?.contentId, data?.lookupNonce, data?.content_type, data?.topic_id, data?.sub_topic_id]);

  const fetchNameOfFile = useCallback(async () => {
    setIsLoading(true);
    
    if (isMCQ(contentType) || isCoding(contentType)) {
      const json = {
        subtopic_id: selectedSubTopic,
        content_type: contentType.toLowerCase(),
      };
      // Add editor field when content type is coding
      if (isCoding(contentType) && selectedEditor) {
        json.editor = selectedEditor;
      }
      try {
        const response = await apiPost(
          `${config.API_BASE_URL}/api/content_creation/questions/`,
          json
        );
        const jsonData = response.data;
        setDisplayFiles(jsonData.questions || []);
      } catch (error) {
      }
    } else if (contentType === ContentTypeConstants.PROJECT_CODING.key) {
      // Fetch project_coding questions from hierarchical API
      try {
        const projectId = sessionStorage.getItem('contentCreation_projectId');
        const projectIdInt = projectId ? parseInt(JSON.parse(projectId)) : null;
        
        if (!projectIdInt) {
          setDisplayFiles([]);
          setIsLoading(false);
          return;
        }

        const json = {
          project_id: projectIdInt,
          question_type: 'project_coding',
          page: 1,
          limit: 100 // Get all questions
        };
        
        const response = await apiPost(
          `${config.API_BASE_URL}/api/get-questions-hierarchical/`,
          json
        );
        
        if (response.data && response.data.questions) {
          // Extract question IDs from the questions array
          const questionIds = response.data.questions.map(q => q.question_id);
          setDisplayFiles(questionIds);
          
          // Store project_image_url if available (used for docker image)
          if (response.data.project_image_url) {
            const imageName = response.data.project_image_url.image_name;
            const imageTag = response.data.project_image_url.image_tag;
            const imageId = response.data.project_image_url.id;
            if (imageName && imageTag) {
              const dockerImage = `${imageName}:${imageTag}`;
              sessionStorage.setItem('projectCoding_dockerImage', dockerImage);
            }
            // Store the image ID for container creation
            if (imageId) {
              sessionStorage.setItem('projectCoding_imageId', String(imageId));
            }
          }
        } else {
          setDisplayFiles([]);
        }
      } catch (error) {
        setDisplayFiles([]);
      }
    } else if (contentType === ContentTypeConstants.NOTEBOOK.key) {
      try {
        const response = await apiPost(
          `${config.API_BASE_URL}/api/content_creation/notebooks/list/`,
          { subtopic_id: selectedSubTopic }
        );
        setDisplayFiles(response.data.notebooks || []);
      } catch (error) {
        setDisplayFiles([]);
      }
    } else if (contentType === ContentTypeConstants.NOTES.key) {
      try {
        const response = await apiPost(
          `${config.API_BASE_URL}/api/content_creation/notes/list/`,
          { subtopic_id: selectedSubTopic }
        );
        const jsonData = response.data;
        setAllNotes(jsonData.notes_by_level || {});
        // Initially show all notes
        const allNotesList = Object.values(jsonData.notes_by_level || {}).flat();
        setFilteredNotes(allNotesList);
        setDisplayFiles(allNotesList);
      } catch (error) {
      }
    } else if (contentType === ContentTypeConstants.VIDEO.key) {
      try {
        const response = await apiPost(
          `${config.API_BASE_URL}/api/content_creation/videos/list/`,
          { subtopic_id: selectedSubTopic }
        );
        const jsonData = response.data;
        setAllVideos(jsonData.videos_by_level || {});
        // Initially show all videos
        const allVideosList = Object.values(jsonData.videos_by_level || {}).flat();
        setFilteredVideos(allVideosList);
        setDisplayFiles(allVideosList);
      } catch (error) {
      }
    }
    setIsLoading(false);
  }, [contentType, selectedSubTopic, selectedEditor]);

  // Function to handle video status API call
  const handleVideoStatus = useCallback(async (videoId) => {
    setIsLoading(true);
    try {
      const response = await apiGet(
        `${config.API_BASE_URL}/api/content_creation/video-status/${videoId}/`
      );
      
      const statusData = response.data;
      
      if (statusData.status === "ready") {
        // Update video details with OTP and playback info
        setVideoDetails(prevDetails => ({
          ...prevDetails,
          video_otp: statusData.vdo_otp,
          video_playback_info: statusData.vdo_playback_info
        }));
      } 
      
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleRadioChangeDiff = (event) => {
    setSelectedDifficultyWithSession(event.target.value);
    setsqlQuery("");
    setsqlQn("");
    setTable("");
    setsqlTags([]);
    setsqlHints([]);
    setsqlExplain([]);
    setSQLTestCases([]);
    // Note: refreshOutput function is not available in this hook context
    // You may need to implement this separately or handle it differently
  };

  const handleNotesLevelChange = (event) => {
    const selectedLevel = event.target.value;
    setNotesLevel(selectedLevel);
    
    if (selectedLevel === "") {
      // Show all notes when no level is selected
      const allNotesList = Object.values(allNotes).flat();
      setFilteredNotes(allNotesList);
      setDisplayFiles(allNotesList);
    } else {
      // Show only notes for the selected level
      const levelNotes = allNotes[selectedLevel] || [];
      setFilteredNotes(levelNotes);
      setDisplayFiles(levelNotes);
    }
  };

  const handleVideoLevelChange = (event) => {
    const selectedLevel = event.target.value;
    setVideoLevel(selectedLevel);
    
    if (selectedLevel === "") {
      // Show all videos when no level is selected
      const allVideosList = Object.values(allVideos).flat();
      setFilteredVideos(allVideosList);
      setDisplayFiles(allVideosList);
    } else {
      // Show only videos for the selected level
      const levelVideos = allVideos[selectedLevel] || [];
      setFilteredVideos(levelVideos);
      setDisplayFiles(levelVideos);
    }
  };

  const handleQuestionChange = useCallback(async (event) => {
    const questionId = event?.target?.value || event;
    const questionIdString = typeof questionId === 'string' ? questionId : String(questionId);
    
    // Prevent loading the same question twice or if already loading
    if (isLoadingQuestion) {
      return;
    }
    
    // If currentFile already matches and we have question text loaded, don't reload
    if (currentFile === questionIdString && questionIdString !== CONSTANTS.DEFAULT_QN && codingQuestionText) {
      return;
    }
    setIsLoadingQuestion(true);
    setCurrentFile(questionIdString);
    setIsLoading(true);
    try {
      if (questionIdString !== CONSTANTS.DEFAULT_QN) {
        // Preserve the question ID in selectedQuestion for tracking
        // Don't overwrite it with question text
        if (contentType === ContentTypeConstants.NOTES.key) {
          // Handle notes selection
          const json = {
            "note_id": event.target.value
          };
          const response = await apiPost(
            `${config.API_BASE_URL}/api/content_creation/notes/detail/`,
            json
          );
          const jsonData = response.data;
          
          // Update notes form fields with the response data
          if (jsonData) {
            setNotesTitle(jsonData.title || "");
            setNotesContent(jsonData.content || "");
            setNotesTime(jsonData.time !== undefined && jsonData.time !== null ? jsonData.time : "");
            setNotesStatus(jsonData.status || CONSTANTS.ACTIVE);
            setNotesSequence(jsonData.sequence !== undefined && jsonData.sequence !== null ? jsonData.sequence : "");
            setNotebookSource(jsonData.notebook_source || "");
            setNotebookId(jsonData.notebook_id || null);
            // setNotesLevel(jsonData.level || "level1");
            const incomingLevel = jsonData.level || "level1";
            const normalizedLevel = String(incomingLevel).toLowerCase();
            const validLevel = ["level1", "level2", "level3"].includes(normalizedLevel) ? normalizedLevel : "level1";
            setSelectedDifficultyWithSession(validLevel);
          }
        } else if (contentType === ContentTypeConstants.NOTEBOOK.key) {
          const response = await apiPost(
            `${config.API_BASE_URL}/api/content_creation/notebooks/detail/`,
            { notebook_id: Number(event.target.value) }
          );
          const jsonData = response.data;
          if (jsonData) {
            setNotebookTitle(jsonData.title || "");
            setNotebookSequence(jsonData.sequence !== undefined && jsonData.sequence !== null ? String(jsonData.sequence) : "1");
            setNotebookTime(jsonData.time !== undefined && jsonData.time !== null ? String(jsonData.time) : "");
            setNotebookStatus(jsonData.status || CONSTANTS.ACTIVE);
            setNotebookSource(jsonData.source || "");
            setNotebookId(jsonData.id || null);
            const incomingLevel = jsonData.level || "level1";
            const normalizedLevel = String(incomingLevel).toLowerCase();
            const validLevel = ["level1", "level2", "level3"].includes(normalizedLevel) ? normalizedLevel : "level1";
            setSelectedDifficultyWithSession(validLevel);
          }
        } else if (contentType === ContentTypeConstants.VIDEO.key) {
          // Handle video selection
          const json = {
            "video_id": String(event.target.value)
          };
          const response = await apiPost(
            `${config.API_BASE_URL}/api/content_creation/videos/detail/`,
            json
          );
          const jsonData = response.data;
          
          // Update video form fields with the response data
          if (jsonData) {
            setVideoTitle(jsonData.title || "");
            setVideoDescription(jsonData.description || "");
            setVideoTime(jsonData.video_duration);
            setVideoStatus(jsonData.status || CONSTANTS.ACTIVE);
            // For sequence, since it's not in the API response, set a default value of 1
            // This ensures the form validation passes
            setVideoSequence("1");
            const incomingLevel = jsonData.level || "level1";
            const normalizedLevel = String(incomingLevel).toLowerCase();
            const validLevel = ["level1", "level2", "level3"].includes(normalizedLevel) ? normalizedLevel : "level1";
            setVideoLevel(validLevel);
            setSelectedDifficultyWithSession(validLevel);
            // Store video details for status button logic
            setVideoDetails(jsonData);
          }
          // Clear file input when switching between videos
          const fileInput = document.getElementById('videoFile');
          if (fileInput) {
            fileInput.value = '';
          }
        } else if (contentType === ContentTypeConstants.PROJECT_CODING.key) {
          // Handle PROJECT_CODING questions
          const json = {
            "question_id": questionIdString
          };
          const response = await apiPost(
            `${config.API_BASE_URL}/api/content_creation/get_specific_question/`,
            json
          );
          let jsonData = response.data.question_data;
          
          // Handle case where question_data might be nested deeper (for assigned content)
          if (jsonData && typeof jsonData === 'object') {
            if (!jsonData.Qn && jsonData.question_data) {
              jsonData = jsonData.question_data;
            }
          }
          
          // Extract and normalize level
          let incomingLevel = jsonData?.Level ?? jsonData?.level ?? jsonData?.difficulty ?? jsonData?.Difficulty ?? null;
          if (incomingLevel && typeof incomingLevel === 'number') {
            incomingLevel = `level${incomingLevel}`;
          }
          
          let validLevel = "level1";
          if (incomingLevel) {
            const normalizedLevel = String(incomingLevel).toLowerCase();
            if (["level1", "level2", "level3"].includes(normalizedLevel)) {
              validLevel = normalizedLevel;
            } else if (normalizedLevel === "easy" || normalizedLevel === "e") {
              validLevel = "level1";
            } else if (normalizedLevel === "medium" || normalizedLevel === "m") {
              validLevel = "level2";
            } else if (normalizedLevel === "hard" || normalizedLevel === "h") {
              validLevel = "level3";
            }
          }
          
          // Ensure selectedQuestion remains as the question ID
          if (selectedQuestion !== questionIdString) {
            setSelectedQuestion(questionIdString);
          }
          
          
          sessionStorage.setItem('currentQuestionId', questionIdString);
          
          // Store question content
          const questionContent = jsonData.Name || jsonData.requirements || "";
          if (questionContent) {
            sessionStorage.setItem('currentQuestionContent', questionContent);
          }
          
          if (jsonData.Qn) {
            sessionStorage.setItem('projectCoding_pageName', jsonData.Qn);
          }
          if (jsonData.Tags && Array.isArray(jsonData.Tags)) {
            sessionStorage.setItem('projectCoding_tags', JSON.stringify(jsonData.Tags));
          }
          if (jsonData.image_urls && Array.isArray(jsonData.image_urls)) {
            sessionStorage.setItem('projectCoding_imageUrls', JSON.stringify(jsonData.image_urls));
          }
          if (jsonData.master_repo_url) {
            sessionStorage.setItem('projectCoding_githubRepoUrl', jsonData.master_repo_url);
          }
          if (jsonData.master_repo_tag) {
            sessionStorage.setItem('projectCoding_githubTag', jsonData.master_repo_tag);
          }
          if (jsonData.docker_image) {
            sessionStorage.setItem('projectCoding_dockerImage', jsonData.docker_image);
          }
          if (jsonData.duration_minutes !== undefined && jsonData.duration_minutes !== null) {
            sessionStorage.setItem('projectCoding_durationMinutes', String(jsonData.duration_minutes));
          }
          if (jsonData.template_repo_url) {
            sessionStorage.setItem('projectCoding_templateRepoUrl', jsonData.template_repo_url);
          }
          if (jsonData.image_id !== undefined && jsonData.image_id !== null) {
            sessionStorage.setItem('projectCoding_imageId', String(jsonData.image_id));
          }
          
          setSelectedDifficultyWithSession(validLevel);
        } else {
          // Handle other content types (MCQ/Coding)
        const json = {
          "question_id": questionIdString
        };
          const response = await apiPost(
          `${config.API_BASE_URL}/api/content_creation/get_specific_question/`,
          json
        );
        let jsonData = response.data.question_data;
        
        // Handle case where question_data might be nested deeper (for assigned content)
        if (jsonData && typeof jsonData === 'object') {
          // If jsonData doesn't have the expected coding question fields, try to unwrap it
          if (!jsonData.Qn && jsonData.question_data) {
            jsonData = jsonData.question_data;
          }
        }
        
        // Extract and normalize level FIRST - used for all content types
        // Check multiple possible field names for level information
        // For assigned content questions, the level might be in different formats
        let incomingLevel = jsonData?.Level ?? jsonData?.level ?? jsonData?.difficulty ?? jsonData?.Difficulty ?? null;
        
        // Handle numeric difficulty levels (e.g., 1, 2, 3) - convert to level1, level2, level3
        if (incomingLevel && typeof incomingLevel === 'number') {
          incomingLevel = `level${incomingLevel}`;
        }
        
        let validLevel = "level1"; // Default to level1 instead of level3
        
        if (incomingLevel) {
          const normalizedLevel = String(incomingLevel).toLowerCase();
          // Also handle "level1", "Level1", "Easy"/"Medium"/"Hard" formats
          if (["level1", "level2", "level3"].includes(normalizedLevel)) {
            validLevel = normalizedLevel;
          } else if (normalizedLevel === "easy" || normalizedLevel === "e") {
            validLevel = "level1";
          } else if (normalizedLevel === "medium" || normalizedLevel === "m") {
            validLevel = "level2";
          } else if (normalizedLevel === "hard" || normalizedLevel === "h") {
            validLevel = "level3";
          }
        }
        
        // Log for debugging);
        
        // IMPORTANT: Preserve question ID in selectedQuestion - don't overwrite with question text
        // The question ID is needed for tracking and the dropdown
        
        if (isCoding(contentType)) {
          // Prefer editor from API so Choose Editor is populated even when session/nav omitted it
          const resolvedEditor =
            normalizeEditorKey(jsonData?.editor) ||
            normalizeEditorKey(selectedEditor);
          if (resolvedEditor && resolvedEditor !== selectedEditor) {
            setSelectedEditorWithSession(resolvedEditor);
          }

          // Use editor type instead of subject name to determine which editor to load
          if (resolvedEditor === EditorConstants.PYTHON_CODING.key) {
            // Store question text in codingQuestionText, keep selectedQuestion as question ID
            // Load requirements if available, otherwise use Qn as requirements
            const requirementsText = jsonData.requirements || jsonData.Qn || "";
            setCodingQuestionText(requirementsText);
            // Also update sessionStorage for requirements
            if (requirementsText) {
              sessionStorage.setItem('currentQuestionContent', requirementsText);
            }
            // Ensure selectedQuestion remains as the question ID (don't overwrite with text)
            if (selectedQuestion !== questionIdString) {
              setSelectedQuestion(questionIdString);
            }
            setdefaultTemplate(jsonData.Template);
            setCode1(jsonData.Ans);
            setCode2(jsonData.FunctionCall);

            // Safely handle TestCases which may be empty or missing for assigned content
            const rawTestCases = Array.isArray(jsonData.TestCases) ? jsonData.TestCases : [];
            const keywordsValue = rawTestCases.length > 0 && rawTestCases[0] && (rawTestCases[0].Testcase || Object.values(rawTestCases[0])[0]) ? (rawTestCases[0].Testcase || Object.values(rawTestCases[0])[0]) : [];
            const parsedTestCases = rawTestCases.length > 1 ? rawTestCases.slice(1).map((tc) => Object.values(tc)[0]) : [];
            setTestCases(parsedTestCases);
            setKeywords(keywordsValue);

            settags(jsonData.Tags || []);

            // Examples, Explanations, Hints may be missing or empty - guard accordingly
            const examplesArr = Array.isArray(jsonData.Examples) ? jsonData.Examples.map((exam) => Object.values(exam)[0]) : [];
            const explanationsArr = Array.isArray(jsonData.Explanations) ? jsonData.Explanations.map((ex) => Object.values(ex)[0]) : [];
            const hintsArr = Array.isArray(jsonData.Hints) ? jsonData.Hints.map((hint) => Object.values(hint)[0]) : [];
            setExamples(examplesArr);
            setExplain(explanationsArr);
            setHints(hintsArr);
          } else if (resolvedEditor === EditorConstants.SQL_CODING.key) {
            settags(jsonData.Tags || []);
            // For SQL, question text goes to sqlqn, not selectedQuestion
            // Keep selectedQuestion as question ID
            // Ensure selectedQuestion remains as the question ID
            if (selectedQuestion !== questionIdString) {
              setSelectedQuestion(questionIdString);
            }
            setsqlQuery(jsonData.Ans);
            setsqlQn(jsonData.Qn);
            setTable(jsonData.Table);
            // Parse table string and set selected tables
            if (jsonData.Table) {
              const tableArray = jsonData.Table.split(',').map(table => table.trim());
              setSelectedTables(tableArray);
            } else {
              setSelectedTables([]);
            }
            setsqlTags(jsonData.Tags || []);
            const rawSqlHints = Array.isArray(jsonData.Hints) ? jsonData.Hints : [];
            setsqlHints(rawSqlHints.map((ex) => Object.values(ex)[0]));
            const rawSqlExpl = Array.isArray(jsonData.Explanations) ? jsonData.Explanations : (Array.isArray(jsonData.Expl) ? jsonData.Expl : []);
            setsqlExplain(rawSqlExpl.map((ex) => Object.values(ex)[0]));
            const rawSqlTestCases = Array.isArray(jsonData.TestCases) ? jsonData.TestCases : [];
            const allTestCases = rawSqlTestCases.map((testCase) => testCase.Testcase);
            
            // Load ExpectedOutput: use ExpectedOutput field if available, otherwise try last test case
            let expectedOutputData = Array.isArray(jsonData.ExpectedOutput) ? jsonData.ExpectedOutput : [];
            if (expectedOutputData.length === 0 && allTestCases.length > 0) {
              const lastTestCase = allTestCases[allTestCases.length - 1];
              // Last test case should be array (same format as ExpectedOutput)
              expectedOutputData = Array.isArray(lastTestCase) ? lastTestCase : [];
            }
            
            setSqlExpectedOutput(expectedOutputData);
            
            // Set test cases: actual test cases + expected output as last item (as array, same format as ExpectedOutput)
            const displayTestCases = allTestCases.length > 0 ? allTestCases.slice(0, -1) : [];
            setSQLTestCases(expectedOutputData.length > 0 ? [...displayTestCases, expectedOutputData] : displayTestCases);
          } else if (resolvedEditor === EditorConstants.FRONTEND_CODING.key) {
            // For HTML/Frontend, question text is handled by window.loadHTMLQuestionData
            // Keep selectedQuestion as question ID
            sessionStorage.setItem('currentQuestionId', questionIdString);
            // Ensure selectedQuestion remains as the question ID
            if (selectedQuestion !== questionIdString) {
              setSelectedQuestion(questionIdString);
            }
            const dataToPass = jsonData.question_data || jsonData;
            if (window.loadHTMLQuestionData) {
              window.loadHTMLQuestionData(dataToPass);
            }
          }
        } else if (isMCQ(contentType)) {
          if (isQuiz(contentType) || (jsonData.content && jsonData.answer)) {
            const parsed = parseQuizQuestionData(jsonData);
            setMCQQuestion(parsed.question);
            setQuizOptions(parsed.options);
            setQuizTemplate(parsed.template);
            setMCQExplanation(parsed.explanation);
            setmcqtags(parsed.tags || []);
          } else {
            setMCQQuestion(jsonData.question);
            setOptions({
              option1: jsonData.options[0],
              option2: jsonData.options[1],
              option3: jsonData.options[2],
              option4: jsonData.options[3],
            });
            setmcqtags(jsonData.Tags || jsonData.tags);
            setMCQExplanation(jsonData.Explanation);
            setSelectedTemplate(jsonData.Template);
          }
        }
        setSelectedDifficultyWithSession(validLevel);
        }
      } else {
        if (contentType === ContentTypeConstants.NOTES.key) {
          // Clear notes form fields when "Create New Notes" is selected
          setNotesTitle("");
          setNotesContent("");
          setNotesTime("");
          setNotesStatus(CONSTANTS.ACTIVE);
          setNotesSequence("");
          setNotesLevel("level1");
          setNotebookSource("");
          setNotebookId(null);
        } else if (contentType === ContentTypeConstants.NOTEBOOK.key) {
          setNotebookTitle("");
          setNotebookSequence("1");
          setNotebookTime("");
          setNotebookStatus(CONSTANTS.ACTIVE);
          setNotebookSource("");
          setNotebookId(null);
        } else if (contentType === ContentTypeConstants.VIDEO.key) {
          // Clear video form fields when "Create New Video" is selected
          setVideoTitle("");
          setVideoDescription("");
          setVideoTime("");
          setVideoStatus(CONSTANTS.ACTIVE);
          setVideoSequence("");
          setVideoLevel("level1");
          setVideoFile(null);
          setVideoDetails(null); // Clear video details to hide video player and status button
          // Clear file input when switching to "Create New Video"
          const fileInput = document.getElementById('videoFile');
          if (fileInput) {
            fileInput.value = '';
          }
        } else if (isMCQ(contentType)) {
          setMCQQuestion("");
          setOptions({
            option1: "",
            option2: "",
            option3: "",
            option4: "",
          });
          setQuizOptions(createDefaultQuizOptions().map((option, index) => ({
            ...option,
            correct: index === 0,
          })));
          setQuizTemplate(QUIZ_TEMPLATES.SINGLE);
          setMCQExplanation("");
          setmcqtags([]);
        } else if (isCoding(contentType)) {
          setdefaultTemplate("");
          setCode1("");
          setCode2("");
          setTestCases([]);
          setKeywords([]);
          settags([]);
          setExamples([]);
          setExplain([]);
          setHints([]);
          setCodingQuestionText("");
          // Don't clear selectedQuestion here - it should remain as the question ID
        }
        
        // Clear question ID from sessionStorage when "Create New" is selected
        sessionStorage.setItem('currentQuestionId', '');
        setSelectedDifficultyWithSession("default");
      }
    } catch (error) {
      setCodingQuestionText("");
      setdefaultTemplate("");
      setCode1("");
      setCode2("");
      setTestCases([]);
      setKeywords([]);
      settags([]);
      setExamples([]);
      setExplain([]);
      setHints([]);
      setSelectedDifficultyWithSession("default");
    } finally {
    setIsLoading(false);
      setIsLoadingQuestion(false);
    }
  }, [contentType, selectedEditor, currentFile, isLoadingQuestion, codingQuestionText, selectedQuestion, setSelectedDifficultyWithSession, setSelectedQuestion, setSelectedEditorWithSession]);

  // Initialize data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await fetchTopics();
        await fetchCount();

        if (selectedEditor === EditorConstants.SQL_CODING.key) {
          const response = await apiGet(`${config.API_BASE_URL}/api/content_creation/tables-data/`);
          const data = response.data.tables;
          setsqlData(data);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fetchTopics, fetchCount, selectedEditor]);

  useEffect(() => {
    fetchNameOfFile();
  }, [contentType, selectedSubTopic, selectedEditor, fetchNameOfFile]);

  // If a coding question was opened before an editor was chosen, reload once editor is set
  useEffect(() => {
    if (
      !isCoding(contentType) ||
      !selectedEditor ||
      !currentFile ||
      currentFile === CONSTANTS.DEFAULT_QN ||
      isLoadingQuestion
    ) {
      return;
    }
    // Only reload when editor content is still empty (first-time pick)
    const hasLoadedContent =
      Boolean(codingQuestionText) ||
      Boolean(sqlqn) ||
      Boolean(htmlContent);
    if (!hasLoadedContent) {
      handleQuestionChange(currentFile);
    }
  }, [selectedEditor]); // intentionally narrow: react to editor pick only

  // Load subtopics when selectedTopic is loaded from session storage
  useEffect(() => {
    if (selectedTopic && topics.length > 0) {
      const topic = topics.find((topic) => String(topic.topic_id) === String(selectedTopic));
      if (topic) {
        setSubTopics(topic.sub_topics);
      }
    }
  }, [selectedTopic, topics]);

  // Function to load video details (extracted from handleQuestionChange)
  const loadVideoDetails = useCallback(async (videoId) => {
    setIsLoading(true);
    try {
      const json = {
        "video_id": String(videoId)
      };
      const response = await apiPost(
        `${config.API_BASE_URL}/api/content_creation/videos/detail/`,
        json
      );
      const jsonData = response.data;
      
      // Update video form fields with the response data
      if (jsonData) {
        setVideoTitle(jsonData.title || "");
        setVideoDescription(jsonData.description || "");
        setVideoTime(jsonData.video_duration);
        setVideoStatus(jsonData.status || CONSTANTS.ACTIVE);
        // For sequence, since it's not in the API response, set a default value of 1
        // This ensures the form validation passes
        setVideoSequence("1");
        setVideoLevel(jsonData.level || "level1");
        setSelectedDifficultyWithSession(jsonData.level || "level1");
        // Store video details for status button logic
        setVideoDetails(jsonData);
      }
      // Clear file input when switching between videos
      const fileInput = document.getElementById('videoFile');
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Function to load note details (similar to loadVideoDetails)
  const loadNoteDetails = useCallback(async (noteId) => {
    setIsLoading(true);
    try {
      const json = {
        "note_id": String(noteId)
      };
      const response = await apiPost(
        `${config.API_BASE_URL}/api/content_creation/notes/detail/`,
        json
      );
      const jsonData = response.data;
      
      // Update note form fields with the response data
      if (jsonData) {
        setNotesTitle(jsonData.title || "");
        setNotesContent(jsonData.content || "");
        setNotesTime(jsonData.time || "");
        setNotesStatus(jsonData.status || CONSTANTS.ACTIVE);
        setNotesSequence(jsonData.sequence || "1");
        setNotesLevel(jsonData.level || "level1");
        setSelectedDifficultyWithSession(jsonData.level || "level1");
        setNotebookSource(jsonData.notebook_source || "");
        setNotebookId(jsonData.notebook_id || null);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadNotebookDetails = useCallback(async (id) => {
    setIsLoading(true);
    try {
      const response = await apiPost(
        `${config.API_BASE_URL}/api/content_creation/notebooks/detail/`,
        { notebook_id: Number(id) }
      );
      const jsonData = response.data;
      if (jsonData) {
        setNotebookTitle(jsonData.title || "");
        setNotebookSequence(jsonData.sequence !== undefined && jsonData.sequence !== null ? String(jsonData.sequence) : "1");
        setNotebookTime(jsonData.time !== undefined && jsonData.time !== null ? String(jsonData.time) : "");
        setNotebookStatus(jsonData.status || CONSTANTS.ACTIVE);
        setNotebookSource(jsonData.source || "");
        setNotebookId(jsonData.id || null);
        const incomingLevel = jsonData.level || "level1";
        const normalizedLevel = String(incomingLevel).toLowerCase();
        const validLevel = ["level1", "level2", "level3"].includes(normalizedLevel) ? normalizedLevel : "level1";
        setSelectedDifficultyWithSession(validLevel);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }, []);

  // After lookup sets hierarchy, wait until topic/subtopic/type are applied then load the item
  useEffect(() => {
    const pending = pendingLookupRef.current;
    if (!pending) return;

    const topicOk = String(selectedTopic) === String(pending.topicId);
    const subOk = String(selectedSubTopic) === String(pending.subTopicId);
    const typeOk = contentType === pending.contentType;
    const editorOk =
      pending.contentType !== ContentTypeConstants.CODING.key ||
      !pending.editor ||
      selectedEditor === pending.editor;
    const topicInList = topics.some(
      (topic) => String(topic.topic_id) === String(pending.topicId)
    );
    const subInList = subTopics.some(
      (subtopic) => String(subtopic.subtopic_id) === String(pending.subTopicId)
    );

    if (!topicOk || !subOk || !typeOk || !editorOk || !topicInList || !subInList) {
      return;
    }

    pendingLookupRef.current = null;

    if (pending.contentType === ContentTypeConstants.VIDEO.key) {
      loadVideoDetails(pending.contentId);
    } else if (pending.contentType === ContentTypeConstants.NOTES.key) {
      loadNoteDetails(pending.contentId);
    } else {
      handleQuestionChange(pending.contentId);
    }
  }, [
    selectedTopic,
    selectedSubTopic,
    contentType,
    selectedEditor,
    topics,
    subTopics,
    handleQuestionChange,
    loadVideoDetails,
    loadNoteDetails,
  ]);

  // Set currentFile when selectedVideo is loaded from session storage
  useEffect(() => {
    if (selectedVideo && contentType === ContentTypeConstants.VIDEO.key) {
      setCurrentFile(selectedVideo);
    }
  }, [selectedVideo, contentType]);

  // Set currentFile when videos are loaded and selectedVideo is available
  useEffect(() => {
    if (selectedVideo && contentType === ContentTypeConstants.VIDEO.key) {
      // If freezeFilters is true, load the video directly even if it's not in DisplayFiles
      // This handles cases where the video might be in a different level or filtered out
      if (freezeFilters) {
        setCurrentFile(selectedVideo);
        loadVideoDetails(selectedVideo);
      } else if (DisplayFiles.length > 0) {
      // Check if the selected video exists in the loaded videos
      const videoExists = DisplayFiles.some(video => String(video.id) === String(selectedVideo));
      if (videoExists) {
        setCurrentFile(selectedVideo);
        // Call the video detail API to load video information
        loadVideoDetails(selectedVideo);
      }
    }
    }
  }, [selectedVideo, contentType, DisplayFiles, loadVideoDetails, freezeFilters]);

  // Set currentFile when selectedNote is loaded from session storage
  useEffect(() => {
    if (selectedNote && contentType === ContentTypeConstants.NOTES.key) {
      setCurrentFile(selectedNote);
    }
  }, [selectedNote, contentType]);

  // Set currentFile when notes are loaded and selectedNote is available
  useEffect(() => {
    if (selectedNote && contentType === ContentTypeConstants.NOTES.key) {
      // If freezeFilters is true, load the note directly even if it's not in DisplayFiles
      // This handles cases where the note might be in a different level or filtered out
      if (freezeFilters) {
        setCurrentFile(selectedNote);
        loadNoteDetails(selectedNote);
      } else if (DisplayFiles.length > 0) {
        // Check if the selected note exists in the loaded notes.map(n => ({ id: n.id, title: n.title })));
      const noteExists = DisplayFiles.some(note => String(note.id) === String(selectedNote));
      if (noteExists) {
        setCurrentFile(selectedNote);
        // Call the note detail API to load note information
        loadNoteDetails(selectedNote);
        } else {
      }
    }
    }
  }, [selectedNote, contentType, DisplayFiles, loadNoteDetails, freezeFilters]);

  useEffect(() => {
    if (selectedNotebook && contentType === ContentTypeConstants.NOTEBOOK.key) {
      setCurrentFile(selectedNotebook);
    }
  }, [selectedNotebook, contentType]);

  useEffect(() => {
    if (selectedNotebook && contentType === ContentTypeConstants.NOTEBOOK.key) {
      if (freezeFilters) {
        setCurrentFile(selectedNotebook);
        loadNotebookDetails(selectedNotebook);
      } else if (DisplayFiles.length > 0) {
        const exists = DisplayFiles.some((nb) => String(nb.id) === String(selectedNotebook));
        if (exists) {
          setCurrentFile(selectedNotebook);
          loadNotebookDetails(selectedNotebook);
        }
      }
    }
  }, [selectedNotebook, contentType, DisplayFiles, loadNotebookDetails, freezeFilters]);

  // Set currentFile when questions are loaded and selectedQuestion is available
  // selectedQuestion should contain the question ID, not the question text
  useEffect(() => {
    const contentTypeLower = String(contentType || '').toLowerCase();
    const isQuestionType = contentType === ContentTypeConstants.MCQ.key || contentType === ContentTypeConstants.QUIZ.key || contentType === ContentTypeConstants.CODING.key
    
    // Only proceed if selectedQuestion contains a valid question ID (not question text)
    // Question IDs are typically short strings like "qpy250514113001chm11", not long text
    const isValidQuestionId = selectedQuestion && selectedQuestion.length < 100 && !selectedQuestion.includes('\n');
    
    if (selectedQuestion && isQuestionType && isValidQuestionId) {
      // If freezeFilters is true, load the question directly even if it's not in DisplayFiles
      // This handles cases where the question might be in a different level or filtered out
      if (freezeFilters) {
        // Only load if:
        // 1. currentFile doesn't match the question ID
        // 2. We're not already loading
        // 3. We don't already have the question text loaded (for coding questions)
        const shouldLoad = currentFile !== selectedQuestion && 
                          !isLoadingQuestion && 
                          (!isCoding(contentType) || !codingQuestionText || codingQuestionText.length === 0);
        
        if (shouldLoad) {
          setCurrentFile(selectedQuestion);
          // Trigger loading of question details for MCQ/Coding
          handleQuestionChange(selectedQuestion);
        } else {
        }
      } else if (DisplayFiles.length > 0) {
      // Check if the selected question exists in the loaded questions
      // We only check for exact matches to avoid false positives when selectedQuestion contains text content
      const questionExists = DisplayFiles.some(question => String(question) === String(selectedQuestion));
      if (questionExists) {
          // Only update if currentFile is not already set to this question and we're not loading
          const shouldLoad = currentFile !== selectedQuestion && 
                            !isLoadingQuestion && 
                            (!isCoding(contentType) || !codingQuestionText || codingQuestionText.length === 0);
          
          if (shouldLoad) {
          setCurrentFile(selectedQuestion);
          // Trigger loading of question details for MCQ/Coding
            handleQuestionChange(selectedQuestion);
        }
      } 
      // Removed fuzzy matching as it causes issues when selectedQuestion contains question text
      // that happens to include an ID from DisplayFiles (e.g. typing "1" in text matches ID "1")
    }
    } else if (selectedQuestion && isQuestionType && !isValidQuestionId) {
      // Invalid question ID format, no action needed
    }
  }, [selectedQuestion, contentType, DisplayFiles, currentFile, handleQuestionChange, freezeFilters, isLoadingQuestion, codingQuestionText]);

  // Computed value for subtopic name
  const selectedSubTopicName = useMemo(() => {
    if (!selectedSubTopic || !subTopics.length) return "";
    const subtopic = subTopics.find(st => st.subtopic_id === selectedSubTopic);
    return subtopic ? subtopic.subtopic_name : "";
  }, [selectedSubTopic, subTopics]);

  // Debug log

  // Return all state and functions
  return {
    // State
    videoFile, setVideoFile,
    videoDescription, setVideoDescription,
    videoTime, setVideoTime,
    videoLevel, setVideoLevel,
    files, setFiles,
    count, setCount,
    explain, setExplain,
    showSucModal, setShowSucModal,
    showEditModal, setShowEditModal,
    selectedDifficulty, setSelectedDifficultyWithSession,
    currentFile, setCurrentFile,
    hints, setHints,
    refreshKey, setRefreshKey,
    isModalOpen, setIsModalOpen,
    testCases, setTestCases,
    keywords, setKeywords,
    tags, settags,
    mcqtags, setmcqtags,
    isLoading, setIsLoading,
    examples, setExamples,
    defaultTemplate, setdefaultTemplate,
    code1, setCode1,
    code2, setCode2,
    DisplayFiles, setDisplayFiles,
    selectedTopic, setSelectedTopicWithSession,
    topics, setTopics,
    subTopics, setSubTopics,
    contentType, setContentTypeWithSession,
    mcqQuestion, setMCQQuestion,
    options, setOptions,
    quizOptions, setQuizOptions,
    quizTemplate, setQuizTemplate,
    error, setError,
    mcqExplanation, setMCQExplanation,
    selectedSubTopic, setSelectedSubTopicWithSession,
    selectedSubTopicName, // Add the computed subtopic name
    SQLTestCases, setSQLTestCases,
    isHtmlModalOpen, setIsHtmlModalOpen,
    htmlContent, setHtmlContent,
    selectedTemplate, setSelectedTemplate,
    sqldata, setsqlData,
    sqlerror, setsqlError,
    selectedTables, setSelectedTables,
    sqlexplain, setsqlExplain,
    sqltags, setsqlTags,
    sqlhints, setsqlHints,
    sqlquery, setsqlQuery,
    sqlqn, setsqlQn,
    sqltable, setTable,
    sqlExpectedOutput, setSqlExpectedOutput,
    isQueryrun, setIsQueryrun,
    loading, setLoading,
    modalMessage, setModalMessage,
    resShowModal, setResShowModal,
    isContentTypeDisabled,
    sub_name: subjectName,
    
    // Notes specific state
    notesLevel, setNotesLevel,
    filteredNotes, setFilteredNotes,
    allNotes, setAllNotes,
    notesTitle, setNotesTitle,
    notesContent, setNotesContent,
    notesTime, setNotesTime,
    notesStatus, setNotesStatus,
    notesSequence, setNotesSequence,
    notebookSource, setNotebookSource,
    notebookId, setNotebookId,
    notebookTitle, setNotebookTitle,
    notebookSequence, setNotebookSequence,
    notebookTime, setNotebookTime,
    notebookStatus, setNotebookStatus,
    selectedNotebook, setSelectedNotebookWithSession,
    
    // Video specific state
    filteredVideos, setFilteredVideos,
    allVideos, setAllVideos,
    videoTitle, setVideoTitle,
    videoStatus, setVideoStatus,
    videoSequence, setVideoSequence,
    videoDetails, setVideoDetails,
    selectedVideo, setSelectedVideoWithSession,
    selectedNote, setSelectedNoteWithSession,
    selectedQuestion, setSelectedQuestionWithSession,
    codingQuestionText, setCodingQuestionText, // Separate state for coding question text
    selectedEditor, setSelectedEditorWithSession,
    
    // Functions
    fetchCount,
    fetchTopics,
    fetchNameOfFile,
    handleQuestionChange,
    handleRadioChangeDiff,
    handleNotesLevelChange,
    handleVideoLevelChange,
    handleVideoStatus,
    loadVideoDetails,
    loadNoteDetails,
    isMCQ,
    isQuiz,
    isCoding,
  };
}; 

